import type { ProductOption } from '../api/adopt';
import { RichText } from '../components/RichText';

/** Franchisee-facing money format: USD $, or "Included" when free. */
export function money(n: number): string {
  return n === 0 ? 'Included' : `$${n.toFixed(2)}`;
}

/** True when there is franchisee-facing legal content to show (clauses). Version alone does not count. */
export function hasFranchiseeTerms(content?: ProductOption['content'] | null): boolean {
  return (content?.clauses?.length ?? 0) > 0;
}

/**
 * One product's franchisee-facing terms card: name, resolved fee, MSA/SOW refs, and clauses.
 * Shared by the adoption flow's review step and the admin "franchisee preview" so both render the
 * exact same thing a franchisee sees for a product. Returns null when there are no terms to show.
 */
export function ProductTermsCard({
  name,
  billingType,
  unitPrice,
  content,
  showPrice = true,
}: {
  name: string;
  billingType: ProductOption['product']['billingType'];
  unitPrice: number;
  content?: ProductOption['content'];
  /** Render the fee. Off hides prices on the franchisee adoption form (portal setting). */
  showPrice?: boolean;
}) {
  if (!hasFranchiseeTerms(content)) return null;

  const refBits = [
    content!.msaRef ? `MSA ${content!.msaRef}` : null,
    content!.sowRef ? `SOW ${content!.sowRef}` : null,
  ].filter(Boolean);

  return (
    <div className="rounded-xl border border-border-subtle bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{name}</span>
        {showPrice && <span className="text-xs text-graphite-500">{money(unitPrice)} {billingType === 'per_store' ? '/store/mo' : ''}</span>}
      </div>
      {refBits.length > 0 && (
        <p className="mt-1 text-xs text-graphite-400">{refBits.join(' · ')}</p>
      )}
      <div className="mt-2 space-y-2 text-xs text-graphite-600">
        {content!.clauses.map((c) => (
          <div key={c.id}>
            <span className="font-medium text-graphite-700">{c.title}</span>
            <RichText html={c.body} className="mt-0.5" />
          </div>
        ))}
      </div>
    </div>
  );
}
